import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCollection, requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';

function uniqueList(values) {
  return [...new Set((Array.isArray(values) ? values : [])
    .map((value) => String(value || '').trim())
    .filter(Boolean))];
}

function normalizeRules(rules, brands) {
  const brandByKey = new Map(brands.map((brand) => [brand.toUpperCase(), brand]));
  return (Array.isArray(rules) ? rules : [])
    .map((rule) => ({
      supplier: String(rule?.supplier || '').trim().toUpperCase(),
      brands: uniqueList(rule?.brands || [])
        .map((brand) => brandByKey.get(brand.toUpperCase()))
        .filter(Boolean),
    }))
    .filter((rule) => rule.supplier && rule.brands.length);
}

function normalizeAttachmentRules(rules, key) {
  return (Array.isArray(rules) ? rules : [])
    .map((rule) => ({
      [key]: String(rule?.[key] || '').trim().toUpperCase(),
      fullFramePhoto: Boolean(rule?.fullFramePhoto),
      defectivePartPhoto: Boolean(rule?.defectivePartPhoto),
      serialNumberRequired: Boolean(rule?.serialNumberRequired),
      mandatoryForm: Boolean(rule?.mandatoryForm),
      purchaseInvoice: Boolean(rule?.purchaseInvoice),
      defectVideoRequired: Boolean(rule?.defectVideoRequired),
    }))
    .filter((rule) => rule[key] && (rule.fullFramePhoto || rule.defectivePartPhoto || rule.serialNumberRequired || rule.mandatoryForm || rule.purchaseInvoice || rule.defectVideoRequired));
}

function EditableList({ title, helperText, values, onChange, disabled }) {
  const add = () => onChange([...values, '']);
  const update = (index, value) => onChange(values.map((item, itemIndex) => itemIndex === index ? value : item));
  const remove = (index) => onChange(values.filter((_item, itemIndex) => itemIndex !== index));

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{helperText}</Typography>
        </Box>
        <IconButton size="small" color="primary" onClick={add} disabled={disabled} aria-label={`Ajouter dans ${title}`}><AddRoundedIcon fontSize="small" /></IconButton>
      </Stack>
      <Stack spacing={0.75} sx={{ maxHeight: 330, overflowY: 'auto', pr: 0.5 }}>
        {values.map((value, index) => (
          <Stack key={`${title}-${index}`} direction="row" spacing={0.75} alignItems="center">
            <TextField size="small" value={value} onChange={(event) => update(index, event.target.value)} disabled={disabled} fullWidth />
            <IconButton size="small" color="error" onClick={() => remove(index)} disabled={disabled} aria-label={`Supprimer ${value || 'la ligne'}`}><DeleteRoundedIcon fontSize="small" /></IconButton>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

export default function LogisticsSupplierBrandsPage() {
  const menuPage = useMenuPage('/settings/logistique/marques');
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [defects, setDefects] = useState([]);
  const [replacementParts, setReplacementParts] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const [completeFrameAllowedSuppliers, setCompleteFrameAllowedSuppliers] = useState([]);
  const [rules, setRules] = useState([]);
  const [brandAttachmentRules, setBrandAttachmentRules] = useState([]);
  const [supplier, setSupplier] = useState('');
  const [availableSelection, setAvailableSelection] = useState([]);
  const [assignedSelection, setAssignedSelection] = useState([]);
  const [availableSearch, setAvailableSearch] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [suppliersPayload, settingsPayload] = await Promise.all([
        requestJson('/suppliers'),
        requestJson('/settings/SAV'),
      ]);
      setSuppliers(uniqueList(getCollection(suppliersPayload)
        .filter((item) => item.active !== false)
        .map((item) => item.name)).sort((a, b) => a.localeCompare(b, 'fr')));
      const nextBrands = uniqueList(settingsPayload?.value?.brands || []).sort((a, b) => a.localeCompare(b, 'fr'));
      setBrands(nextBrands);
      setDefects(uniqueList(settingsPayload?.value?.defects || []));
      setReplacementParts(uniqueList(settingsPayload?.value?.replacementParts || []));
      setWarranties(uniqueList(settingsPayload?.value?.warranties || []));
      setCompleteFrameAllowedSuppliers(uniqueList(settingsPayload?.value?.completeFrameAllowedSuppliers || ['LUXOTTICA', 'SAFILO']));
      setRules(normalizeRules(settingsPayload?.value?.supplierBrandRules, nextBrands));
      setBrandAttachmentRules(normalizeAttachmentRules(settingsPayload?.value?.brandAttachmentRules, 'brand'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const normalizedSupplier = supplier.trim().toUpperCase();
  const assignedBrands = useMemo(() => normalizedSupplier
    ? rules.find((rule) => rule.supplier === normalizedSupplier)?.brands || []
    : [], [normalizedSupplier, rules]);
  const assignedKeys = useMemo(() => new Set(assignedBrands.map((brand) => brand.toUpperCase())), [assignedBrands]);
  const availableBrands = useMemo(() => brands.filter((brand) => !assignedKeys.has(brand.toUpperCase())), [assignedKeys, brands]);
  const filteredAvailable = useMemo(() => {
    const needle = availableSearch.trim().toLocaleLowerCase('fr');
    return availableBrands.filter((brand) => !needle || brand.toLocaleLowerCase('fr').includes(needle));
  }, [availableBrands, availableSearch]);

  const toggle = (value, setter) => setter((current) => current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value]);

  const updateAssigned = (nextBrands) => {
    const nextRules = rules.filter((rule) => rule.supplier !== normalizedSupplier);
    if (nextBrands.length) nextRules.push({ supplier: normalizedSupplier, brands: uniqueList(nextBrands) });
    setRules(normalizeRules(nextRules, brands));
  };

  const addSelected = () => {
    updateAssigned([...assignedBrands, ...availableSelection]);
    setAvailableSelection([]);
  };

  const removeSelected = () => {
    const removed = new Set(assignedSelection.map((brand) => brand.toUpperCase()));
    updateAssigned(assignedBrands.filter((brand) => !removed.has(brand.toUpperCase())));
    setAssignedSelection([]);
  };

  const addBrand = () => {
    const value = newBrand.trim();
    if (!value) return;
    setBrands((current) => uniqueList([...current, value]).sort((a, b) => a.localeCompare(b, 'fr')));
    setNewBrand('');
  };

  const attachmentRule = (collection, key, value) => collection.find((rule) => rule[key] === String(value || '').trim().toUpperCase()) || {};
  const updateAttachmentRule = (collection, setCollection, key, value, field, checked) => {
    const normalizedValue = String(value || '').trim().toUpperCase();
    const withoutCurrent = collection.filter((rule) => rule[key] !== normalizedValue);
    const current = attachmentRule(collection, key, value);
    const next = {
      [key]: normalizedValue,
      fullFramePhoto: Boolean(current.fullFramePhoto),
      defectivePartPhoto: Boolean(current.defectivePartPhoto),
      serialNumberRequired: Boolean(current.serialNumberRequired),
      mandatoryForm: Boolean(current.mandatoryForm),
      purchaseInvoice: Boolean(current.purchaseInvoice),
      defectVideoRequired: Boolean(current.defectVideoRequired),
      [field]: checked,
    };
    setCollection(next.fullFramePhoto || next.defectivePartPhoto || next.serialNumberRequired || next.mandatoryForm || next.purchaseInvoice || next.defectVideoRequired ? [...withoutCurrent, next] : withoutCurrent);
  };

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');
      const normalizedBrands = uniqueList(brands).sort((a, b) => a.localeCompare(b, 'fr'));
      const payload = await requestJson('/settings/SAV', {
        method: 'PUT',
        body: JSON.stringify({
          label: 'Paramètres Logistique',
          value: {
            defects: uniqueList(defects),
            replacementParts: uniqueList(replacementParts),
            brands: normalizedBrands,
            warranties: uniqueList(warranties),
            completeFrameAllowedSuppliers: uniqueList(completeFrameAllowedSuppliers),
            brandAttachmentRules: normalizeAttachmentRules(brandAttachmentRules, 'brand'),
            supplierBrandRules: normalizeRules(rules, normalizedBrands),
          },
        }),
      });
      const savedBrands = uniqueList(payload?.value?.brands || normalizedBrands).sort((a, b) => a.localeCompare(b, 'fr'));
      setBrands(savedBrands);
      setRules(normalizeRules(payload?.value?.supplierBrandRules, savedBrands));
      setDefects(uniqueList(payload?.value?.defects || defects));
      setReplacementParts(uniqueList(payload?.value?.replacementParts || replacementParts));
      setWarranties(uniqueList(payload?.value?.warranties || warranties));
      setCompleteFrameAllowedSuppliers(uniqueList(payload?.value?.completeFrameAllowedSuppliers || completeFrameAllowedSuppliers));
      setBrandAttachmentRules(normalizeAttachmentRules(payload?.value?.brandAttachmentRules || brandAttachmentRules, 'brand'));
      setMessage('Paramètres Logistique enregistrés.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const renderList = (values, selected, setSelected, emptyLabel) => (
    <List dense disablePadding sx={{ height: 280, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
      {values.map((brand) => (
        <ListItem key={brand} disablePadding>
          <ListItemButton dense disabled={!normalizedSupplier} onClick={() => toggle(brand, setSelected)}>
            <ListItemIcon sx={{ minWidth: 34 }}><Checkbox size="small" checked={selected.includes(brand)} /></ListItemIcon>
            <ListItemText primary={brand} primaryTypographyProps={{ variant: 'body2' }} />
          </ListItemButton>
        </ListItem>
      ))}
      {!values.length && <ListItem><ListItemText primary={emptyLabel} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} /></ListItem>}
    </List>
  );

  if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}><CircularProgress /></Box>;

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>{menuPage.sectionTitle || 'Paramètres Logistique'}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>{menuPage.pageTitle || 'Marques par fournisseur'}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshRoundedIcon />} onClick={load}>Rafraîchir</Button>
          <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={save} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2.25}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SettingsRoundedIcon color="primary" />
              <Box><Typography variant="h6" sx={{ fontWeight: 900 }}>Listes Logistique</Typography><Typography variant="body2" color="text.secondary">Valeurs disponibles dans le formulaire de demande Logistique.</Typography></Box>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2.5 }}>
              <EditableList title="Défauts" helperText="Liste du champ Défaut." values={defects} onChange={setDefects} disabled={saving} />
              <EditableList title="Pièces à remplacer" helperText="Liste du champ Pièce à remplacer." values={replacementParts} onChange={setReplacementParts} disabled={saving} />
              <EditableList title="Marques" helperText="Liste du champ Marque." values={brands} onChange={setBrands} disabled={saving} />
              <EditableList title="Garanties" helperText="Liste du champ Garantie." values={warranties} onChange={setWarranties} disabled={saving} />
            </Box>
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Fournisseurs autorisés — monture complète/nue</Typography>
              <Typography variant="caption" color="text.secondary">
                Exceptions autorisées sous Garantie fournisseur. Sélectionnez un ou plusieurs fournisseurs existants.
              </Typography>
              <Autocomplete
                multiple
                size="small"
                options={suppliers}
                value={completeFrameAllowedSuppliers.filter((name) => suppliers.includes(name))}
                onChange={(_event, values) => setCompleteFrameAllowedSuppliers(uniqueList(values))}
                disabled={saving}
                disableCloseOnSelect
                sx={{ mt: 1, maxWidth: 820 }}
                renderInput={(params) => <TextField {...params} label="Fournisseurs autorisés" placeholder="Rechercher un fournisseur" />}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2.25}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SettingsRoundedIcon color="primary" />
              <Box><Typography variant="h6" sx={{ fontWeight: 900 }}>Marques par fournisseur</Typography><Typography variant="body2" color="text.secondary">Associe les marques qu’un fournisseur est autorisé à traiter.</Typography></Box>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Autocomplete options={suppliers} value={supplier} onChange={(_event, value) => { setSupplier(value || ''); setAvailableSelection([]); setAssignedSelection([]); }} renderInput={(params) => <TextField {...params} label="Fournisseur" placeholder="Rechercher un fournisseur" />} sx={{ minWidth: { sm: 340 }, flex: 1 }} />
              <TextField label="Nouvelle marque" value={newBrand} onChange={(event) => setNewBrand(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addBrand(); } }} sx={{ minWidth: { sm: 260 } }} />
              <Button variant="outlined" onClick={addBrand} disabled={!newBrand.trim()}>Ajouter la marque</Button>
            </Stack>
            {!normalizedSupplier && <Alert severity="info">Sélectionne un fournisseur pour définir ses marques.</Alert>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 64px minmax(0, 1fr)' }, gap: 1.25, alignItems: 'center' }}>
              <Box><Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.75 }}><Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Marques disponibles</Typography><TextField size="small" placeholder="Rechercher" value={availableSearch} onChange={(event) => setAvailableSearch(event.target.value)} sx={{ width: 180 }} /></Stack>{renderList(filteredAvailable, availableSelection, setAvailableSelection, 'Aucune marque disponible.')}</Box>
              <Stack spacing={1} alignItems="center"><IconButton color="primary" onClick={addSelected} disabled={!normalizedSupplier || !availableSelection.length} aria-label="Associer les marques"><ArrowForwardRoundedIcon /></IconButton><IconButton color="primary" onClick={removeSelected} disabled={!normalizedSupplier || !assignedSelection.length} aria-label="Retirer les marques"><ArrowBackRoundedIcon /></IconButton></Stack>
              <Box><Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>Marques associées</Typography>{renderList(assignedBrands, assignedSelection, setAssignedSelection, 'Aucune marque associée.')}</Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2.25}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SettingsRoundedIcon color="primary" />
              <Box><Typography variant="h6" sx={{ fontWeight: 900 }}>Pièces jointes obligatoires par marque</Typography><Typography variant="body2" color="text.secondary">Les règles fournisseur se gèrent directement dans la fiche Fournisseur.</Typography></Box>
            </Stack>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', maxHeight: 440, overflowY: 'auto' }}>
                <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover' }}><Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Par marque</Typography></Box>
                {brands.map((item) => {
                  const rule = attachmentRule(brandAttachmentRules, 'brand', item);
                  return <Box key={item} sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}><Typography variant="body2" sx={{ fontWeight: 800 }}>{item}</Typography><FormControlLabel control={<Checkbox size="small" checked={Boolean(rule.fullFramePhoto)} onChange={(event) => updateAttachmentRule(brandAttachmentRules, setBrandAttachmentRules, 'brand', item, 'fullFramePhoto', event.target.checked)} disabled={saving} />} label="Photo monture complète" /><FormControlLabel control={<Checkbox size="small" checked={Boolean(rule.defectivePartPhoto)} onChange={(event) => updateAttachmentRule(brandAttachmentRules, setBrandAttachmentRules, 'brand', item, 'defectivePartPhoto', event.target.checked)} disabled={saving} />} label="Photo pièce défectueuse" /><FormControlLabel control={<Checkbox size="small" checked={Boolean(rule.serialNumberRequired)} onChange={(event) => updateAttachmentRule(brandAttachmentRules, setBrandAttachmentRules, 'brand', item, 'serialNumberRequired', event.target.checked)} disabled={saving} />} label="Numéro de série obligatoire" /><FormControlLabel control={<Checkbox size="small" checked={Boolean(rule.mandatoryForm)} onChange={(event) => updateAttachmentRule(brandAttachmentRules, setBrandAttachmentRules, 'brand', item, 'mandatoryForm', event.target.checked)} disabled={saving} />} label="Formulaire obligatoire" /><FormControlLabel control={<Checkbox size="small" checked={Boolean(rule.purchaseInvoice)} onChange={(event) => updateAttachmentRule(brandAttachmentRules, setBrandAttachmentRules, 'brand', item, 'purchaseInvoice', event.target.checked)} disabled={saving} />} label="Facture d’achat client/fournisseur" /><FormControlLabel control={<Checkbox size="small" checked={Boolean(rule.defectVideoRequired)} onChange={(event) => updateAttachmentRule(brandAttachmentRules, setBrandAttachmentRules, 'brand', item, 'defectVideoRequired', event.target.checked)} disabled={saving} />} label="Vidéo du défaut obligatoire" /></Box>;
                })}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
